import React, { useState } from 'react';
import { LayoutListIcon, Columns3Icon } from 'lucide-react';
import type { MetricType, TimeframeType } from '../../types';
import { Header } from '../layout/Header';
import { PeriodSelector } from './PeriodSelector';
import { MetricSummary } from './MetricSummary';
import { DivisionSection } from './DivisionSection';
import { KanbanBoard } from './KanbanBoard';
import { getDivisions, getCurrentPeriod } from '../../data/mockData';
import { formatMetricLabel } from '../../utils/calculations';
export function DashboardPage() {
  const [timeframe, setTimeframe] = useState<TimeframeType>('weekly');
  const [metric, setMetric] = useState<MetricType>('softPull');
  const [viewMode, setViewMode] = useState<'hierarchy' | 'kanban'>('hierarchy');
  const [expandedDivisions, setExpandedDivisions] = useState<Record<string, boolean>>({
    'div-east': true,
    'div-west': true
  });
  const period = getCurrentPeriod(timeframe);
  const divisions = getDivisions(timeframe);
  const toggleDivision = (divId: string) => {
    setExpandedDivisions(prev => ({
      ...prev,
      [divId]: !prev[divId]
    }));
  };
  return <div className="min-h-screen" data-id="element-6">
      <Header title="Performance Dashboard" subtitle="Track goal progress across divisions, teams, and loan officers" data-id="element-7" />

      <div className="px-6 lg:px-8 py-6 space-y-6" data-id="element-8">
        {/* Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-9">
          <PeriodSelector period={period} onPeriodChange={setTimeframe} data-id="element-10" />

          {/* Metric Toggle */}
          <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1" role="tablist" aria-label="Select metric" data-id="element-11">
            <button role="tab" aria-selected={metric === 'softPull'} onClick={() => setMetric('softPull')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${metric === 'softPull' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`} data-id="element-12">
              Soft Pull
            </button>
            <button role="tab" aria-selected={metric === 'submitted'} onClick={() => setMetric('submitted')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${metric === 'submitted' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`} data-id="element-13">
              Submitted
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <MetricSummary divisions={divisions} period={period} metric={metric} data-id="element-14" />

        {/* Section Label + View Toggle */}
        <div className="flex items-center justify-between gap-4" data-id="element-15">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-16">
            {formatMetricLabel(metric)} —{' '}
            {timeframe === 'weekly' ? 'Weekly' : 'Monthly'} Progress
          </h3>

          <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1" role="tablist" aria-label="Select view mode" data-id="element-17">
            <button role="tab" aria-selected={viewMode === 'hierarchy'} onClick={() => setViewMode('hierarchy')} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${viewMode === 'hierarchy' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`} data-id="element-18">
              <LayoutListIcon className="w-4 h-4" data-id="element-19" />
              Hierarchy
            </button>
            <button role="tab" aria-selected={viewMode === 'kanban'} onClick={() => setViewMode('kanban')} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`} data-id="element-20">
              <Columns3Icon className="w-4 h-4" data-id="element-21" />
              Status Board
            </button>
          </div>
        </div>

        {/* Conditional View */}
        {viewMode === 'hierarchy' ? <div className="space-y-4" data-id="element-22">
            {divisions.map(division => <DivisionSection key={division.id} division={division} period={period} metric={metric} expanded={expandedDivisions[division.id] ?? true} onToggle={() => toggleDivision(division.id)} data-id="element-23" />)}
          </div> : <KanbanBoard divisions={divisions} period={period} metric={metric} data-id="element-24" />}
      </div>
    </div>;
}